/**
 * Hono Action Routes.
 *
 * Mounts standalone module-scope actions. Each action: auth → custom
 * middleware → execute handler. Batch actions also expose an SSE `/stream`
 * endpoint via `streamSse`.
 */

import type { MiddlewareHandler } from 'hono'
import type {
  ActionDefinition,
  ModuleContext
} from '@gzl10/nexus-sdk'
import { resolveLocalized } from '@gzl10/nexus-sdk'
import { ZodError } from 'zod'
import type { HonoApp, NexusContext, NexusEnv } from '../types.js'
import { createHonoRouter } from '../types.js'
import { streamSse, createBatchReporter } from '../sse.js'

type NexusMw = MiddlewareHandler<NexusEnv>

/** Cast legacy Express middleware from ctx.core.middleware into a Hono MiddlewareHandler. */
function toHonoMw(mw: unknown): NexusMw {
  return mw as NexusMw
}

/**
 * Create router for all standalone actions in a module.
 */
export function createActionRouters(
  ctx: ModuleContext,
  actions: ActionDefinition[]
): HonoApp {
  const router = createHonoRouter()

  for (const action of actions) {
    const actionRouter = createActionRouter(ctx, action)
    router.route(`/${action.key}`, actionRouter)
  }

  return router
}

/**
 * Create router for a single standalone action.
 */
function createActionRouter(
  ctx: ModuleContext,
  action: ActionDefinition
): HonoApp {
  const router = createHonoRouter()
  const method = action.method ?? 'POST'

  const coreAuth = ctx.core.middleware['auth']
  const coreOptionalAuth = ctx.core.middleware['optionalAuth']

  const handlers: NexusMw[] = []

  if (action.skipAuth) {
    if (coreOptionalAuth) {
      handlers.push(toHonoMw(coreOptionalAuth))
    }
  } else if (coreAuth) {
    // Apply auth globally on this action router
    router.use(toHonoMw(coreAuth))
  }

  if (action.middleware) {
    const mw = action.middleware(ctx)
    if (Array.isArray(mw)) handlers.push(...mw.map(toHonoMw))
    else handlers.push(toHonoMw(mw))
  }

  const executeHandler = createExecuteHandler(ctx, action)
  const chain = [...handlers, async (c: NexusContext) => executeHandler(c)] as NexusMw[]

  // Hono's method overloads don't narrow well with spread — use `.on()`.
  router.on(method, ['/'], ...chain)

  // SSE stream route for batch actions
  if (action.batch) {
    const batchChain: NexusMw[] = []
    if (action.middleware) {
      const mw = action.middleware(ctx)
      if (Array.isArray(mw)) batchChain.push(...mw.map(toHonoMw))
      else batchChain.push(toHonoMw(mw))
    }
    batchChain.push(async (c: NexusContext) => createBatchHandler(ctx, action)(c))
    router.on('GET', ['/stream'], ...batchChain)
  }

  return router
}

/**
 * Create execute handler for a standalone action.
 */
function createExecuteHandler(
  ctx: ModuleContext,
  action: ActionDefinition
): (c: NexusContext) => Promise<Response> {
  const caslSubject = action.casl && 'subject' in action.casl
    ? action.casl.subject
    : `Action:${action.key}`
  const hasCasl = !action.skipAuth && !!action.casl

  return async (c: NexusContext): Promise<Response> => {
    const user = c.get('user')
    const ability = c.get('ability')

    if (hasCasl && ability) {
      const { ForbiddenError: CASLForbiddenError } = ctx.core.abilities
      const caslAction = (action.casl && 'action' in action.casl)
        ? action.casl.action ?? 'execute'
        : 'execute'
      try {
        CASLForbiddenError.from(ability).throwUnlessCan(caslAction, caslSubject as unknown as string)
      } catch {
        throw new ctx.core.errors.ForbiddenError(
          `Not allowed to execute ${resolveLocalized(action.label, 'en')}`
        )
      }
    }

    let input: unknown = {}
    const contentType = c.req.header('content-type') ?? ''
    if (contentType.includes('application/json')) {
      try {
        const body = await c.req.json<unknown>()
        input = body ?? {}
      } catch {
        input = {}
      }
    }

    if (action.inputSchema) {
      try {
        input = action.inputSchema.parse(input)
      } catch (err) {
        const label = resolveLocalized(action.label, 'en')
        if (err instanceof ZodError) {
          throw new ctx.core.errors.ValidationError(
            `Invalid input for action ${label}`,
            err.errors.map(e => ({
              path: e.path.join('.'),
              message: e.message,
              code: 'VALIDATION_FIELD_INVALID'
            }))
          )
        }
        throw err
      }
    } else if (action.input) {
      const violations: Array<{ path: string; message: string; code: string }> = []
      const inputRecord = (input ?? {}) as Record<string, unknown>
      for (const [key, field] of Object.entries(action.input)) {
        // File inputs arrive via multipart/form-data, not JSON body — skip required check here.
        // The handler itself validates file presence via c.req.parseBody().
        if ((field as { input?: string }).input === 'file') continue
        const value = inputRecord[key]
        if (field.required && (value === undefined || value === null || value === '')) {
          violations.push({ path: key, message: 'Field is required', code: 'VALIDATION_FIELD_REQUIRED' })
        } else if (value !== undefined && value !== null) {
          const ft = field.input
          if (ft && ['text', 'email', 'password', 'textarea'].includes(ft as string) && typeof value !== 'string') {
            violations.push({ path: key, message: 'Must be a string', code: 'VALIDATION_FIELD_INVALID' })
          } else if (ft === 'number' && typeof value !== 'number') {
            violations.push({ path: key, message: 'Must be a number', code: 'VALIDATION_FIELD_INVALID' })
          }
        }
      }
      if (violations.length > 0) {
        throw new ctx.core.errors.ValidationError(
          `Invalid input for action ${resolveLocalized(action.label, 'en')}`,
          violations
        )
      }
    }

    let extendedInput = (input ?? {}) as Record<string, unknown>
    if (user?.id) {
      extendedInput = { ...extendedInput, _authUserId: user.id }
    }

    if (!action.handler) {
      throw new ctx.core.errors.AppError(
        `No handler defined for action ${resolveLocalized(action.label, 'en')}`,
        500
      )
    }

    const result = await action.handler(ctx, extendedInput, c)

    const successStatus = (action.successStatus ?? 200) as Parameters<NexusContext['json']>[1]
    if (result !== undefined) {
      return c.json(result as Record<string, unknown> | unknown[], successStatus)
    }
    return c.body(null, successStatus)
  }
}

/**
 * Create SSE batch stream handler for a standalone action.
 */
function createBatchHandler(
  ctx: ModuleContext,
  action: ActionDefinition
): (c: NexusContext) => Promise<Response> {
  return async (c: NexusContext): Promise<Response> => {
    return streamSse(c, async (sender) => {
      const reporter = createBatchReporter(sender)
      try {
        const queryAll = c.req.query()
        const user = c.get('user')
        const input: Record<string, unknown> = { ...queryAll, _batchReporter: reporter }
        if (user?.id) {
          input['_authUserId'] = user.id
        }
        if (action.handler) {
          await action.handler(ctx, input, c)
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unknown error'
        ctx.core.logger.error({ action: action.key, error: message }, 'Batch action failed')
        reporter.fail('BATCH_ERROR', message)
      }
    })
  }
}
